import gql from 'graphql-tag';
import { USER_ROLES } from 'src/_shared/constants/userRolesEnum';

// Constants
export const ACTION_TYPES = {
	CUSTOM_ACTION: 'CUSTOM_ACTION',
	FIRST_MOBILE_LOGIN: 'FIRST_MOBILE_LOGIN',
	WEEKLY_LOGIN_STREAK: 'WEEKLY_LOGIN_STREAK',
	REFERRAL_LINK_SHARED: 'REFERRAL_LINK_SHARED',
	REFERRAL_FROM_SHARE: 'REFERRAL_FROM_SHARE',
	REFERRAL_SENT: 'REFERRAL_SENT',
	REFERRAL_GENERAL_SENT: 'REFERRAL_GENERAL_SENT',
	REFERRAL_TO_ACCEPTED: 'REFERRAL_TO_ACCEPTED',
	REFERRAL_TO_INTERVIEWING: 'REFERRAL_TO_INTERVIEWING',
	REFERRAL_TO_HIRED: 'REFERRAL_TO_HIRED',
	RECOGNITION_SENT: 'RECOGNITION_SENT',
	RECOGNITION_RECEIVED: 'RECOGNITION_RECEIVED',
};

export const POINT_CATEGORIES = {
	ENGAGEMENT: 'ENGAGEMENT',
	RECOGNITION: 'RECOGNITION',
	REFERRAL: 'REFERRAL',
	CUSTOM: 'CUSTOM',
};

export const CRITERIA_GROUPS = [
	'ROLE',
	'DEPARTMENT',
	'SUB_COMPANY',
	'USER_GROUP',
];

export const PRIMARY_PERIODS = {
	ALLTIME: 'Never Reset',
	YEARLY: 'Yearly',
	QUARTERLY: 'Quarterly',
	MONTHLY: 'Monthly',
};

export const ACTION_LABELS = {
	FIRST_MOBILE_LOGIN: 'First Mobile App Login',
	WEEKLY_LOGIN_STREAK: 'Weekly Login Streak',
	REFERRAL_LINK_SHARED: 'Social Media Share',
	REFERRAL_FROM_SHARE: 'Referral From Social Share',
	REFERRAL_SENT: 'Referral Sent',
	REFERRAL_GENERAL_SENT: 'General Referral Sent',
	REFERRAL_TO_ACCEPTED: 'Referral Accepted',
	REFERRAL_TO_INTERVIEWING: 'Referral Moves To Interviewing',
	REFERRAL_TO_HIRED: 'Referral Moves To Hired',
	RECOGNITION_SENT: 'Recognition Sent',
	RECOGNITION_RECEIVED: 'Recognition Received',
};

export const ROLE_OPTIONS = [
	{ value: USER_ROLES.EMPLOYEE, label: 'Employee' },
	{ value: USER_ROLES.MANAGER, label: 'Manager' },
	{ value: USER_ROLES.ADMIN, label: 'Admin' },
];

// GraphQL Queries
export const QueryCompanyEntities = gql`
	query QueryCompanyEntities($companyId: ID!) {
		queryCompanyEntities(companyId: $companyId) {
			companyId
			departments {
				id
				name
			}
			subCompanies {
				id
				name
			}
			userGroups {
				id
				name
			}
			metadata
		}
	}
`;

export const GetPointsSettings = gql`
	query GetPointsSettings($companyId: ID!) {
		getPointsSettings(companyId: $companyId) {
			actions {
				actionId
				actionType
				amount
				customRules {
					amount
					customActionRuleId
					criteria {
						departmentIds
						roles
						userGroupIds
						subCompanyIds
					}
					description
					redeemable
					label
				}
				category
				label
				redeemable
			}
			companyId
			createdAt
			excludeRecognitionPointsFromReset
			excludeSpendablePointsFromReset
			exclusions {
				departmentIds
				roles
				userGroupIds
				subCompanyIds
			}
			customActions {
				actionType
				amount
				category
				customActionId
				customRules {
					amount
					customActionRuleId
					criteria {
						departmentIds
						subCompanyIds
						roles
						userGroupIds
					}
					description
					label
					redeemable
				}
				description
				label
				redeemable
			}
			groupPriority
			maxRedeemable30
			maxRedeemable365
			maxWeeklyShares
			pointsRatio
			primaryPeriod
			redeemable
			updatedAt
		}
	}
`;

export const UpdatePointsSettings = gql`
	mutation UpdatePointsSettings($companyId: ID!, $input: PointsSettingsInput!) {
		updatePointsSettings(companyId: $companyId, input: $input) {
			actions {
				actionId
				actionType
				amount
				customRules {
					amount
					customActionRuleId
					criteria {
						departmentIds
						roles
						userGroupIds
						subCompanyIds
					}
					description
					redeemable
					label
				}
				category
				label
				redeemable
			}
			companyId
			createdAt
			excludeRecognitionPointsFromReset
			excludeSpendablePointsFromReset
			exclusions {
				departmentIds
				roles
				userGroupIds
				subCompanyIds
			}
			customActions {
				actionType
				amount
				category
				customActionId
				customRules {
					amount
					customActionRuleId
					criteria {
						departmentIds
						subCompanyIds
						roles
						userGroupIds
					}
					description
					label
					redeemable
				}
				description
				label
				redeemable
			}
			groupPriority
			maxRedeemable30
			maxRedeemable365
			maxWeeklyShares
			pointsRatio
			primaryPeriod
			redeemable
			updatedAt
		}
	}
`;

export const QueryPointsEventsByCompanyId = gql`
	query QueryPointsEventsByCompanyId(
		$companyId: ID!
		$first: Int
		$after: String
	) {
		queryPointsEventsByCompanyId(
			companyId: $companyId
			first: $first
			after: $after
		) {
			items {
				id
				actionType
				actorUserId
				actorUser {
					id
					firstName
					lastName
					emailAddress
				}
				amount
				companyId
				createdAt
				direction
				isSpendable
				message
				periodMonth
				periodQuarter
				pointsCategory
				periodYear
				referral {
					id
					jobId
					contactId
					contact {
						id
						firstName
						lastName
						emailAddress
						phoneNumber
					}
				}
				revoked
				revokedByUserId
				revokedByUser {
					id
					firstName
					lastName
					emailAddress
				}
				revokeReason
				sourceId
				sourceType
				user {
					id
					firstName
					lastName
					emailAddress
				}
				userDepartmentId
				userId
				userRole
				userSubCompanyId
				userUserGroupId
			}
			nextToken
		}
	}
`;

export const QueryPointsEventsPostgres = gql`
	query QueryPointsEventsPostgres(
		$companyId: ID!
		$first: Int
		$after: String
		$allTime: Boolean
		$year: String
		$quarter: String
		$month: String
		$range: [String]
	) {
		queryPointsEventsPostgres(
			companyId: $companyId
			first: $first
			after: $after
			allTime: $allTime
			year: $year
			quarter: $quarter
			month: $month
			range: $range
		) {
			nextToken
			items {
				id
				actionType
				actorUserId
				actorUser {
					id
					firstName
					lastName
					emailAddress
				}
				amount
				companyId
				createdAt
				direction
				message
				periodMonth
				isSpendable
				periodYear
				periodQuarter
				pointsCategory
				userDepartmentId
				userId
				userSubCompanyId
				userRole
				userUserGroupId
				user {
					emailAddress
					firstName
					id
					lastName
				}
				referral {
					contactId
					contact {
						emailAddress
						firstName
						id
						lastName
						phoneNumber
					}
					id
					jobId
				}
				revoked
				revokedByUserId
				revokedByUser {
					id
					firstName
					lastName
					emailAddress
				}
				revokeReason
				sourceId
				sourceType
			}
		}
	}
`;

export const QueryPointsEventsByUserId = gql`
	query QueryPointsEventsByUserId($userId: ID!, $first: Int, $after: String) {
		queryPointsEventsByUserId(userId: $userId, first: $first, after: $after) {
			nextToken
			items {
				id
				actionType
				actorUserId
				actorUser {
					id
					firstName
					lastName
					emailAddress
				}
				amount
				companyId
				createdAt
				direction
				isSpendable
				message
				periodMonth
				periodQuarter
				periodYear
				pointsCategory
				revoked
				revokedByUserId
				revokedByUser {
					id
					firstName
					lastName
					emailAddress
				}
				revokeReason
				sourceId
				sourceType
				userDepartmentId
				userId
				userRole
				userSubCompanyId
				userUserGroupId
			}
		}
	}
`;

export const CreatePointsTriggerEvent = gql`
	mutation CreatePointsTriggerEvent($input: CreatePointsTriggerEventInput!) {
		createPointsTriggerEvent(input: $input) {
			id
			eventId
			companyId
			targetId
		}
	}
`;

export const CreatePointsRevokeEvent = gql`
	mutation CreatePointsRevokeEvent($input: CreatePointsRevokeEventInput!) {
		createPointsRevokeEvent(input: $input) {
			id
			eventId
			companyId
			targetId
		}
	}
`;

// Helper Functions

// The actual reversal entry that a revoke produces (a DEBIT sourced from
// "Revoke"). These are never themselves revocable.
export const isRevokeDebit = (event) =>
	event?.direction === 'DEBIT' && event?.sourceType === 'Revoke';

// A log row is eligible for a revoke button when it is neither the reversal
// DEBIT entry nor an already-revoked original.
export const isRevocableEvent = (event) =>
	Boolean(event) && !isRevokeDebit(event) && event.revoked !== true;

export const formatUserName = (user) => {
	if (!user) return '';
	return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
};

// Resolve who is attributed to an event and the associated reason/message so
// the logs can render revoke and custom-trigger context consistently.
//   - Reversal DEBIT entry:   actor = actorUser,     reason = message
//   - Revoked original entry: actor = revokedByUser, reason = revokeReason
//   - Custom trigger entry:   actor = actorUser,     note   = message
export const getEventAttribution = (event) => {
	if (!event) return null;
	if (isRevokeDebit(event)) {
		return {
			type: 'REVOKE_DEBIT',
			user: event.actorUser,
			userId: event.actorUserId,
			reason: event.message,
		};
	}

	if (event.revoked === true) {
		return {
			type: 'REVOKED_ORIGINAL',
			user: event.revokedByUser,
			userId: event.revokedByUserId,
			reason: event.revokeReason,
		};
	}

	if (event.actorUser || event.actorUserId) {
		return {
			type: 'TRIGGERED',
			user: event.actorUser,
			userId: event.actorUserId,
			reason: event.message,
		};
	}

	return null;
};

export const formatDate = (dateString) => {
	const date = new Date(dateString);
	return date.toLocaleString('en-US', {
		month: 'numeric',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	});
};

export const formatDateUTC = (dateString) => {
	const date = new Date(dateString);
	return (
		date.toLocaleString('en-US', {
			month: 'numeric',
			day: 'numeric',
			year: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
			timeZone: 'UTC',
		}) + ' UTC'
	);
};

export const formatActionTypeForExport = (actionType, event) => {
	if (!actionType) return 'unknown action';
	const contact = event.referral?.contact;
	const contactName = contact ? `${contact.firstName} ${contact.lastName}` : '';
	const contactEmail = contact?.emailAddress || contact?.phoneNumber || '';

	let reason;
	switch (actionType) {
		case 'REFERRAL_SENT':
			reason = `sending a referral to ${contactName} (${contactEmail})`;
			break;
		case 'REFERRAL_GENERAL_SENT':
			reason = `a referral that was accepted by ${contactName} (${contactEmail})`;
			break;
		case 'REFERRAL_TO_ACCEPTED':
			reason = `a referral to ${contactName} (${contactEmail}) being accepted`;
			break;
		case 'REFERRAL_TO_INTERVIEWING':
			reason = `a referral to ${contactName} (${contactEmail}) moving to interviewing`;
			break;
		case 'REFERRAL_TO_HIRED':
			reason = `a referral to ${contactName} (${contactEmail}) being hired`;
			break;
		case 'REFERRAL_LINK_SHARED':
			reason = 'sharing a referral link';
			break;
		case 'REFERRAL_FROM_SHARE':
			reason = 'a referral from a social media share';
			break;
		case 'FIRST_MOBILE_LOGIN':
			reason = 'a first mobile login';
			break;
		case 'WEEKLY_LOGIN_STREAK':
			reason = 'a weekly login streak';
			break;
		case 'RECOGNITION_SENT':
			reason = 'sending recognition';
			break;
		case 'RECOGNITION_RECEIVED':
			reason = 'receiving recognition';
			break;
		case 'POINTS_FROM_BADGE':
			reason = 'the points value set on a received recognition';
			break;
		case 'POINTS_SPENT':
			reason =
				event.direction === 'CREDIT' ? 'a points refund' : 'spending points';
			break;
		default:
			reason = actionType.toLowerCase().replace(/_/g, ' ');
			break;
	}

	if (
		event.direction === 'DEBIT' &&
		event.sourceType === 'Revoke' &&
		actionType !== 'POINTS_SPENT'
	) {
		reason += ' that was revoked';
	}

	return reason;
};
